/**
 * Reading ruler for the roster grids — like pressing a cell in Excel and having its row and
 * column light up: tap a cell and a horizontal band runs across its row and a vertical band
 * down its column (plus a marker on the time header and a tint on the row label), so you can
 * trace a cell back to its counter/officer and its time. Off until switched on with
 * RulerToggle; then tap the same cell again, press Escape, or tap another cell to
 * move/clear it, or switch it off to get the plain grid back.
 *
 * Purely visual: the bands ignore the pointer and the click is only observed (capture phase),
 * never stopped, so every existing grid gesture keeps working underneath.
 */
import { RULER_DIM_FAR, RULER_DIM_NEAR, rulerColor, setRulerEnabled, useRulerEnabled, type RulerCell } from '@/utils/readingRuler';

/** The two bands, to sit inside the canvas's own `position: relative` wrapper. */
export function ReadingRulerBands({
  ruler, rowTop, rowHeight, cellWidth, totalWidth, totalHeight,
}: {
  ruler: RulerCell | null;
  rowTop: number;
  rowHeight: number;
  cellWidth: number;
  totalWidth: number;
  totalHeight: number;
}) {
  if (!ruler) return null;
  // Focus + context: the pressed cell stays clear, its row and column are dimmed lightly, and the
  // four corner blocks outside the cross are dimmed more — so the eye is led to the cross.
  const x0 = ruler.col * cellWidth;
  const x1 = x0 + cellWidth;
  const y0 = rowTop;
  const y1 = rowTop + rowHeight;
  const rect = (id: string, left: number, top: number, width: number, height: number, alpha: number) =>
    width > 0 && height > 0 ? (
      <div key={id} data-testid={id} style={{ position: 'absolute', pointerEvents: 'none', zIndex: 2, left, top, width, height,
        background: `rgba(15, 23, 42, ${alpha})` }} />
    ) : null;
  return (
    <>
      {rect('ruler-dim-tl', 0, 0, x0, y0, RULER_DIM_FAR)}
      {rect('ruler-dim-tr', x1, 0, totalWidth - x1, y0, RULER_DIM_FAR)}
      {rect('ruler-dim-bl', 0, y1, x0, totalHeight - y1, RULER_DIM_FAR)}
      {rect('ruler-dim-br', x1, y1, totalWidth - x1, totalHeight - y1, RULER_DIM_FAR)}
      {rect('ruler-row-l', 0, y0, x0, rowHeight, RULER_DIM_NEAR)}
      {rect('ruler-row-r', x1, y0, totalWidth - x1, rowHeight, RULER_DIM_NEAR)}
      {rect('ruler-col-t', x0, 0, cellWidth, y0, RULER_DIM_NEAR)}
      {rect('ruler-col-b', x0, y1, cellWidth, totalHeight - y1, RULER_DIM_NEAR)}
    </>
  );
}

/** A small tick on the time header at the pressed column. */
export function RulerHeaderMarker({ ruler, cellWidth, mode }: { ruler: RulerCell | null; cellWidth: number; mode?: string | null }) {
  if (!ruler) return null;
  return (
    <div data-testid="ruler-mark" style={{ position: 'absolute', left: ruler.col * cellWidth, width: cellWidth, bottom: 0, height: 6,
      background: rulerColor(mode), pointerEvents: 'none', zIndex: 1 }} />
  );
}


/** Switch for the ruler, shared by both grids. Solid blue while on, dashed grey with a slash while off; off means taps leave the grid alone. */
export function RulerToggle({ size = 22 }: { size?: number }) {
  const on = useRulerEnabled();
  return (
    <button
      type="button"
      data-testid="ruler-toggle"
      aria-pressed={on}
      onClick={() => setRulerEnabled(!on)}
      title={on ? 'Ruler is ON — tap a cell to highlight its row and column. Tap to turn off' : 'Ruler: turn on, then tap a cell to highlight its row and column'}
      aria-label="Ruler"
      style={{
        width: size, height: size, flexShrink: 0, padding: 0, cursor: 'pointer', borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: on ? '#2563eb' : '#ffffff', color: on ? '#ffffff' : '#94a3b8',
        border: on ? '1px solid #1d4ed8' : '1px dashed #94a3b8',
        boxShadow: on ? '0 0 0 2px rgba(37,99,235,0.3)' : 'none',
      }}
    >
      {/* A ruler: a bar with ticks. Filled when the highlight is on. */}
      <svg width={Math.round(size * 0.68)} height={Math.round(size * 0.68)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="10" rx="1.5" fill={on ? 'currentColor' : 'none'} fillOpacity={on ? 0.3 : 0} />
        <line x1="6" y1="7" x2="6" y2="11" /><line x1="10" y1="7" x2="10" y2="12" /><line x1="14" y1="7" x2="14" y2="11" /><line x1="18" y1="7" x2="18" y2="12" />
        {!on && <line x1="3" y1="21" x2="21" y2="3" stroke="#94a3b8" strokeWidth="2.5" />}
      </svg>
    </button>
  );
}
